import { Project, ProjectStatus } from 'interfaces/project'
import { IconFlyoutMenu } from 'lib/components/dropdown-icon-menu/icon-flyout-menu'
import DeleteProjectFlyoutButton from './delete-project-flyout-button'
import { CopyIcon, FolderOpenIcon, PlusIcon } from 'lucide-react'
import ProjectDrawer, { ProjectUpdateType } from './project-drawer/project-drawer'
import { useEffect, useState } from 'react'
import { updateParams } from 'lib/util/url'
import { HorizontalCard } from 'lib/components/card/card'
import { useMutation } from '@tanstack/react-query'
import { request } from 'lib/api/fetch-api'
import { toast } from 'sonner'
import { PATHS } from 'lib/constants/paths'
import { StateScreen } from 'lib/components/state-screen/state-screen'
import ProgressBar from 'lib/components/progress-bar/progress-bar'
import Tooltip from 'lib/components/tooltip/tooltip'
import { getBadgeColor, getBadgeLabel } from './project-status-popover'
import Badge from 'lib/components/badge/badge'

export interface ProjectListProps {
  projects: Project[]
  onUpdate?: (project: Project, action: ProjectUpdateType) => void
}

/**
 * @deprecated this has been migrated to the web app and any changes should be made in the web app and here
 */
export default function ProjectList({ projects, onUpdate }: ProjectListProps) {
  const [isProjectDrawerOpen, setIsProjectDrawerOpen] = useState(false)
  const projectId = parseInt(new URLSearchParams(window.location.search).get('projectId')) || null

  const handleProjectClick = (project: Project) => {
    const params = new URLSearchParams(window.location.search)
    params.set('projectId', project.id.toString())
    updateParams(params)
    setIsProjectDrawerOpen(true)
  }

  function handleSetIsProjectDrawerOpen(open: boolean) {
    const params = new URLSearchParams(window.location.search)
    if (!open) {
      params.delete('projectId')
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
      setIsProjectDrawerOpen(false)
    }

    setIsProjectDrawerOpen(open)
  }

  const FlyoutMenu = ({ project }: { project: Project }) => {
    const duplicateProjectMutation = useMutation({
      mutationFn: () => {
        return request({
          endpoint: 'duplicateProject',
          query: { id: project.id },
        })
      },
      onSuccess: (response: { data: { id: number } }) => {
        toast.success('Project has been duplicated')
        window.location.href = `${PATHS.REQUESTS}?tab=draft&projectId=${response.data.id}&duplicate=true`
      },
      onError: () => {
        toast.error('Failed to duplicate project')
      },
    })

    const openAddRequestsDrawer = () => {
      const params = new URLSearchParams(window.location.search)
      params.set('projectId', project.id.toString())
      params.set('projectTab', 'add-deliverables')
      updateParams(params)
      setIsProjectDrawerOpen(true)
    }

    return (
      <IconFlyoutMenu color="transparent" icon={['far', 'ellipsis-v']}>
        <IconFlyoutMenu.Button className="tw-flex tw-items-center tw-gap-2" onClick={openAddRequestsDrawer}>
          <PlusIcon className="tw-h-4 tw-w-4" />
          <span className="">Add requests</span>
        </IconFlyoutMenu.Button>
        <IconFlyoutMenu.Button className="tw-flex tw-items-center tw-gap-2" onClick={duplicateProjectMutation.mutate}>
          <CopyIcon className="tw-h-4 tw-w-4" />
          <span className="">Duplicate</span>
        </IconFlyoutMenu.Button>
        <DeleteProjectFlyoutButton project={project} onSuccess={() => onUpdate(project, ProjectUpdateType.DELETE)} />
      </IconFlyoutMenu>
    )
  }

  const ProjectCard = ({ project }: { project: Project }) => {
    const bars = [
      {
        progress: (project?.counters?.doneTickets / project?.counters?.totalTickets) * 100,
        color: '#00AA2D',
      },
    ]

    return (
      <HorizontalCard onClick={() => handleProjectClick(project)}>
        <>
          <div className="tw-flex tw-w-full tw-flex-col tw-justify-center tw-gap-1 tw-overflow-hidden tw-p-2 tw-pl-4">
            <h4 className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold">
              <div className="tw-flex tw-w-full tw-items-center tw-gap-1">
                {project.ticketsRequiringAttentionCount > 0 && (
                  <div className="tw-flex tw-aspect-square tw-h-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-flushpink-500 tw-text-xs tw-text-white">
                    {project.ticketsRequiringAttentionCount}
                  </div>
                )}
                <div className="tw-truncate">
                  <Tooltip content={project.name}>
                    <div>{project.name}</div>
                  </Tooltip>
                </div>
                {project.status !== ProjectStatus.NoStatus && (
                  <Badge color={getBadgeColor(project.status)} style="pill">
                    {getBadgeLabel(project.status)}
                  </Badge>
                )}
              </div>
            </h4>
            <div className="tw-truncate tw-text-xs">{project.description}</div>
            {project?.counters && (
              <Tooltip content={`Done: ${project.counters.doneTickets} of ${project.counters.totalTickets} requests`}>
                <ProgressBar size="sm" bars={bars} />
              </Tooltip>
            )}
          </div>
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center" onClick={(e) => e.stopPropagation()}>
            <FlyoutMenu project={project} />
          </div>
        </>
      </HorizontalCard>
    )
  }

  useEffect(() => {
    const urlState = new URLSearchParams(window.location.search)
    setIsProjectDrawerOpen(urlState.get('projectId') !== null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search])

  return (
    <>
      <ProjectDrawer
        isOpen={isProjectDrawerOpen}
        setOpen={handleSetIsProjectDrawerOpen}
        projectId={projectId}
        onUpdate={onUpdate}
      />
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-pb-2">
        {projects?.length === 0 && (
          <StateScreen
            Icon={FolderOpenIcon}
            title="You don't have any projects yet"
            subTitle="Organize and bring your creative vision to life. Start with expert-crafted design recipes, explore
              ready-to-go templates, or customize your own project to fit your needs."
            buttons={[
              {
                text: 'Get Started with Projects',
                onClick: () => window.location.assign('/create?tab=projects'),
                color: 'neutralGray',
              },
            ]}
          />
        )}
        {projects?.map((project) => <ProjectCard project={project} key={project.id} />)}
      </div>
    </>
  )
}
